import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';

interface AnimatedCirclesProps {
  isMaximized: boolean;
  isCalling: boolean;
  audioLevel?: number;
  variant: 'large' | 'small';
  animate?: boolean;
}

// Simple pulse hook: animates a value between 0 and 2 and back to 0.
// durationMs controls the time to go from 0 -> 2 or 2 -> 0 (one leg of the cycle).
function usePulseAnimation(enabled: boolean, durationMs = 1000): number {
  const [level, setLevel] = useState(0);
  const rafIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) {
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      setLevel(0);
      return;
    }

    let direction = 1; // 1 = increasing, -1 = decreasing
    let lastTime = performance.now();
    let current = 0;

    const step = (ts: number) => {
      const dt = ts - lastTime;
      lastTime = ts;

      // Progress per ms: 1 over durationMs
      current += direction * (dt / durationMs);

      if (current >= 2) {
        current = 2;
        direction = -1;
      } else if (current <= 0) {
        current = 0;
        direction = 1;
      }

      setLevel(current);
      rafIdRef.current = requestAnimationFrame(step);
    };

    rafIdRef.current = requestAnimationFrame(step);

    return () => {
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    };
  }, [enabled, durationMs]);

  return level;
}

export const AnimatedCircles = ({
  isMaximized,
  isCalling,
  audioLevel,
  variant,
  animate = false,
}: AnimatedCirclesProps) => {
  const animatedLevel = usePulseAnimation(animate, 1000);
  const active = animate ? true : isCalling;
  const level = animate ? animatedLevel : (audioLevel ?? 0);
  const sizes = {
    large: {
      container: isMaximized ? 'w-[326px] h-[326px]' : 'w-[173px] h-[173px]',
      circles: {
        four: {
          width: isMaximized ? '326px' : '173px',
          height: isMaximized ? '326px' : '173px',
          left: '0',
          top: '0',
        },
        three: {
          width: isMaximized ? '288px' : '152px',
          height: isMaximized ? '288px' : '152px',
          left: isMaximized ? '19px' : '10px',
          top: isMaximized ? '19px' : '10px',
        },
        two: {
          width: isMaximized ? '263px' : '138px',
          height: isMaximized ? '263px' : '138px',
          left: isMaximized ? '31.5px' : '17.5px',
          top: isMaximized ? '31.5px' : '17.5px',
        },
        one: {
          width: isMaximized ? '173px' : '92px',
          height: isMaximized ? '172px' : '92px',
          left: isMaximized ? '76.5px' : '40px',
          top: isMaximized ? '77px' : '40px',
        },
      },
    },
    small: {
      container: 'w-[144px] h-[144px]',
      circles: {
        four: { width: '144px', height: '144px', left: '0', top: '0' },
        three: { width: '127px', height: '127px', left: '8.5px', top: '8.5px' },
        two: { width: '115px', height: '115px', left: '14.5px', top: '14.5px' },
        one: { width: '77px', height: '77px', left: '33.5px', top: '33.5px' },
      },
    },
  };

  const currentSize = sizes[variant];

  return (
    <div className={clsx('relative', currentSize.container)}>
      <div
        className="absolute transition-all"
        style={{
          backgroundImage: `url(/assets/onboarding/call-circle-four.png)`,
          backgroundSize: '100% 100%',
          ...currentSize.circles.four,
          opacity: active ? 0.5 + level * 0.1 : 1,
          transform: active ? `scale(${1 + level * 0.01})` : 'scale(1)',
          transition: active
            ? 'opacity 0.15s ease-out, transform 0.15s ease-out'
            : 'opacity 0.5s ease-in-out, transform 0.5s ease-in-out',
        }}
      />
      <div
        className="absolute transition-all"
        style={{
          backgroundImage: `url(/assets/onboarding/call-circle-three.png)`,
          backgroundSize: '100% 100%',
          ...currentSize.circles.three,
          opacity: active ? 0.7 + level * 0.06 : 1,
          transform: active ? `scale(${1 + level * 0.025})` : 'scale(1)',
          transition: active
            ? 'opacity 0.2s ease-out, transform 0.2s ease-out'
            : 'opacity 0.6s ease-in-out, transform 0.6s ease-in-out',
        }}
      />
      <div
        className="absolute transition-all"
        style={{
          backgroundImage: `url(/assets/onboarding/call-circle-two.png)`,
          backgroundSize: '100% 100%',
          ...currentSize.circles.two,
          transform: active ? `scale(${1 - level * 0.018})` : 'scale(1)',
          transition: active
            ? 'transform 0.1s ease-out'
            : 'transform 0.7s ease-in-out',
        }}
      />
      <div
        className="absolute transition-all"
        style={{
          backgroundImage: `url(/assets/onboarding/call-circle-one.png)`,
          backgroundSize: '100% 100%',
          ...currentSize.circles.one,
        }}
      />
    </div>
  );
};
